import React, { useCallback, useEffect, useRef, useState } from "react";

// The five stages of the AI-native production pipeline. Descriptions use a
// noun-style, process-oriented tone (no imperatives) — we're not selling a
// tool, we're showing how our production process works.
const SLIDES = [
  {
    id: "script",
    short: "Script",
    label: "Script Breakdown",
    caption:
      "Scripts parsed into hooks, beats, shot logic and cliffhangers.",
    src: "https://customer-assets.emergentagent.com/job_vertical-studio-1/artifacts/dghomfc4_1Script%20parsed.png",
  },
  {
    id: "character",
    short: "Character",
    label: "Character Continuity",
    caption:
      "Consistent characters across scenes, angles and episodes.",
    src: "https://customer-assets.emergentagent.com/job_vertical-studio-1/artifacts/3g6vkt9m_1NewCharacter.png",
  },
  {
    id: "location",
    short: "Location",
    label: "Location Coverage",
    caption:
      "Controllable locations across multiple viewpoints.",
    src: "https://customer-assets.emergentagent.com/job_vertical-studio-1/artifacts/8gl50s4i_1Location.png",
  },
  {
    id: "props",
    short: "Props",
    label: "Prop Continuity",
    caption:
      "Reusable hero props maintained across the story world.",
    src: "https://customer-assets.emergentagent.com/job_vertical-studio-1/artifacts/0udmqfr6_1Props.png",
  },
  {
    id: "scene",
    short: "Scene",
    label: "Scene Timeline",
    caption:
      "Shot-by-shot control of AI-generated scenes.",
    src: "https://customer-assets.emergentagent.com/job_vertical-studio-1/artifacts/531hn3vr_1Timeline.png",
  },
];

const AUTOPLAY_MS = 5000;

// A quiet, autoplay-looping preview of our internal AI timeline tool. Sits to
// the right of the AI copy on desktop, aligned with the top of the section
// heading; stacks below on mobile. Refined glass frame, no player UI,
// "Internal build" label at the base. The source is 2000×1416 (landscape),
// so we keep the natural aspect ratio and never crop into a portrait card.
// Click the frame (or the small expand chip) to open a large lightbox view.
const INTERNAL_BUILD_SRC =
  "https://customer-assets.emergentagent.com/job_vertical-studio-1/artifacts/nrmv5ssz_AI_Livingroom_vertical_3.mp4";

const InternalBuildLightbox = ({ open, onClose }) => {
  const videoRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (open) {
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
  }, [open]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Internal build preview"
      data-testid="ai-internal-build-lightbox"
      onClick={onClose}
      className="fixed inset-0 flex items-center justify-center px-4 md:px-10"
      style={{
        background: "rgba(4,4,4,0.88)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        zIndex: 100,
        animation: "lr-fade-in 220ms ease forwards",
      }}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close preview"
        data-testid="ai-internal-build-close"
        className="absolute top-6 right-6 md:top-8 md:right-10 flex items-center justify-center"
        style={{
          width: 44,
          height: 44,
          borderRadius: 999,
          background: "rgba(15,15,15,0.75)",
          border: "1px solid #1f1f1f",
          color: "#F5F5F3",
          cursor: "pointer",
          zIndex: 2,
        }}
      >
        <span aria-hidden style={{ fontSize: 18, lineHeight: 1 }}>×</span>
      </button>

      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full"
        style={{
          maxWidth: "min(1400px, 96vw)",
          aspectRatio: "2000 / 1416",
          maxHeight: "88vh",
          borderRadius: "20px",
          overflow: "hidden",
          background: "#0a0a0a",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow:
            "0 60px 160px -30px rgba(0,0,0,0.95), 0 0 0 1px rgba(255,255,255,0.03) inset, 0 0 80px -20px rgba(255,106,61,0.12)",
        }}
        data-testid="ai-internal-build-lightbox-frame"
      >
        <video
          ref={videoRef}
          src={INTERNAL_BUILD_SRC}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          controlsList="nodownload noplaybackrate noremoteplayback"
          disablePictureInPicture
          draggable={false}
          aria-hidden
          className="block w-full h-full"
          style={{ objectFit: "contain", pointerEvents: "none" }}
        />
        <div
          aria-hidden
          className="absolute bottom-0 left-0 right-0 pointer-events-none"
          style={{
            height: "18%",
            background:
              "linear-gradient(180deg, rgba(8,8,8,0) 0%, rgba(8,8,8,0.5) 60%, rgba(8,8,8,0.85) 100%)",
          }}
        />
        <span
          className="absolute pointer-events-none text-[10px] tracking-[0.32em] uppercase"
          style={{
            right: "22px",
            bottom: "18px",
            color: "rgba(245,245,243,0.7)",
            fontWeight: 500,
          }}
        >
          Internal build
        </span>
      </div>
    </div>
  );
};

const InternalBuildVideo = () => {
  const videoRef = React.useRef(null);
  const wrapRef = React.useRef(null);
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const el = wrapRef.current;
    const v = videoRef.current;
    if (!el || !v || typeof IntersectionObserver === "undefined") return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const p = v.play();
            if (p && typeof p.catch === "function") p.catch(() => {});
          } else {
            v.pause();
          }
        });
      },
      { threshold: 0.2 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <div
        ref={wrapRef}
        data-testid="ai-internal-build-video"
        onClick={() => setOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(true);
          }
        }}
        aria-label="Open internal build preview"
        className="relative w-full lr-internal-build-frame"
        style={{
          aspectRatio: "2000 / 1416",
          borderRadius: "20px",
          overflow: "hidden",
          border: "1px solid rgba(255,255,255,0.08)",
          background:
            "linear-gradient(180deg, rgba(20,20,20,0.85) 0%, rgba(11,11,11,0.9) 100%)",
          boxShadow:
            "0 30px 100px -30px rgba(0,0,0,0.9), 0 0 0 1px rgba(255,255,255,0.03) inset, 0 0 60px -25px rgba(255,106,61,0.08)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          cursor: "pointer",
        }}
      >
        <video
          ref={videoRef}
          src={INTERNAL_BUILD_SRC}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          controlsList="nodownload noplaybackrate noremoteplayback"
          disablePictureInPicture
          draggable={false}
          aria-hidden
          className="block w-full h-full"
          style={{ objectFit: "cover", pointerEvents: "none" }}
        />

        <div
          aria-hidden
          className="absolute bottom-0 left-0 right-0 pointer-events-none"
          style={{
            height: "22%",
            background:
              "linear-gradient(180deg, rgba(8,8,8,0) 0%, rgba(8,8,8,0.5) 60%, rgba(8,8,8,0.8) 100%)",
          }}
        />

        {/* Small "expand" chip in the top-right corner — hint that this
            preview can be opened larger. Grows softly on hover. */}
        <div
          aria-hidden
          className="absolute top-3 right-3 flex items-center justify-center lr-internal-build-expand"
          data-testid="ai-internal-build-expand"
          style={{
            width: 34,
            height: 34,
            borderRadius: 999,
            background: "rgba(10,10,10,0.55)",
            border: "1px solid rgba(255,255,255,0.12)",
            color: "rgba(245,245,243,0.85)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            zIndex: 3,
            transition:
              "background 220ms ease, transform 220ms ease, border-color 220ms ease",
          }}
        >
          {/* Two arrows expanding outward — pure SVG, no icon lib needed. */}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <polyline points="4 10 4 4 10 4" />
            <polyline points="20 14 20 20 14 20" />
            <line x1="4" y1="4" x2="10" y2="10" />
            <line x1="20" y1="20" x2="14" y2="14" />
          </svg>
        </div>

        <span
          className="absolute pointer-events-none text-[10px] tracking-[0.32em] uppercase"
          style={{
            right: "18px",
            bottom: "14px",
            color: "rgba(245,245,243,0.65)",
            fontWeight: 500,
            zIndex: 2,
          }}
          data-testid="ai-internal-build-label"
        >
          Internal build
        </span>
      </div>

      <InternalBuildLightbox open={open} onClose={() => setOpen(false)} />
    </>
  );
};
// Wheel accumulator: how many px of vertical wheel must add up before the
// slideshow advances by one stage. Higher = feels more intentional / less
// twitchy.
const WHEEL_STEP = 90;
// Cooldown after a step change during which further wheel input is absorbed
// (preventDefault) but ignored — prevents rapid multi-advances from a single
// trackpad flick.
const STEP_COOLDOWN_MS = 550;

// Coverflow-style Apple-keynote slideshow. One active slide in a glass
// frame, previous / next peek behind at low opacity + scale + blur.
// Autoplay + hover-pause + wheel-to-advance while the module is centered.
const PipelineSlider = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef(null);
  const containerRef = useRef(null);
  // Wheel-hijack machinery
  const centeredRef = useRef(false); // module >= 75% visible
  const wheelAccumRef = useRef(0);
  const lastStepAtRef = useRef(0);
  // Latest active — read from inside the wheel handler without re-subscribing.
  const activeRef = useRef(0);
  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  useEffect(() => {
    SLIDES.forEach((s) => {
      const img = new Image();
      img.src = s.src;
    });
  }, []);

  // Auto-advance while not paused / not hovered.
  useEffect(() => {
    if (paused) return undefined;
    const t = setTimeout(
      () => setActive((a) => (a + 1) % SLIDES.length),
      AUTOPLAY_MS
    );
    return () => clearTimeout(t);
  }, [active, paused]);

  // Track whether the module is centered in the viewport (drives both
  // autoplay-pause-when-offscreen and the wheel hijack).
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const centered = entry.intersectionRatio >= 0.75;
          centeredRef.current = centered;
          // Pause autoplay when not on screen at all.
          if (entry.intersectionRatio < 0.2) setPaused(true);
          else if (!centered) setPaused(false);
        });
      },
      { threshold: [0, 0.2, 0.5, 0.75, 1] }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Wheel hijack: while the module is centered, vertical wheel scrolls
  // through the pipeline stages first. Only page-scrolls once we're past
  // the last / before the first slide. Desktop only.
  useEffect(() => {
    const canHover =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(hover: hover)").matches;
    if (!canHover) return undefined;

    const onWheel = (e) => {
      if (!centeredRef.current) return;
      // Ignore mostly-horizontal gestures (trackpad users navigating sideways).
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      const a = activeRef.current;
      const goingDown = e.deltaY > 0;
      const atEnd = goingDown ? a >= SLIDES.length - 1 : a <= 0;
      if (atEnd) return; // release — let the page scroll normally

      // We're advancing / rewinding through the pipeline. Absorb the scroll.
      e.preventDefault();

      const now = performance.now();
      if (now - lastStepAtRef.current < STEP_COOLDOWN_MS) return;

      wheelAccumRef.current += e.deltaY;
      if (Math.abs(wheelAccumRef.current) >= WHEEL_STEP) {
        const dir = wheelAccumRef.current > 0 ? 1 : -1;
        const next = Math.max(0, Math.min(SLIDES.length - 1, a + dir));
        if (next !== a) setActive(next);
        wheelAccumRef.current = 0;
        lastStepAtRef.current = now;
      }
    };

    window.addEventListener("wheel", onWheel, { passive: false });
    return () => window.removeEventListener("wheel", onWheel);
  }, []);

  const goto = useCallback(
    (i) => setActive(((i % SLIDES.length) + SLIDES.length) % SLIDES.length),
    []
  );

  const onTouchStart = (e) => {
    touchX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) goto(active + (dx < 0 ? 1 : -1));
    touchX.current = null;
  };

  return (
    <div
      ref={containerRef}
      data-testid="ai-pipeline-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Kicker */}
      <div
        className="text-[11px] tracking-[0.32em] uppercase mb-6 md:mb-8"
        style={{ color: "#A1A1AA", fontWeight: 500 }}
      >
        <span style={{ color: "#FF6A3D" }}>—</span>&nbsp;&nbsp;Script. Character. Location. Props. Scene.
      </div>

      {/* PIPELINE STEPPER — the visible flow. Also the primary navigation. */}
      <div
        className="w-full overflow-x-auto no-scrollbar mb-10 md:mb-14"
        style={{ scrollbarWidth: "none" }}
      >
        <div
          className="flex items-center gap-3 md:gap-5"
          style={{ minWidth: "max-content" }}
          data-testid="ai-pipeline-stepper"
        >
          {SLIDES.map((s, i) => {
            const isActive = i === active;
            return (
              <React.Fragment key={s.id}>
                <button
                  type="button"
                  onClick={() => goto(i)}
                  data-testid={`ai-pipeline-step-${s.id}`}
                  data-active={isActive ? "true" : "false"}
                  className="relative shrink-0 text-left"
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: "6px 2px 12px",
                    cursor: "pointer",
                  }}
                >
                  <div
                    className="flex items-baseline gap-2"
                    style={{
                      transition: "opacity 300ms ease",
                      opacity: isActive ? 1 : 0.55,
                    }}
                  >
                    <span
                      className="text-[9px] tracking-[0.32em] uppercase"
                      style={{
                        color: isActive ? "#FF6A3D" : "#4d4d4d",
                        fontWeight: 500,
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span
                      className="text-xs md:text-sm"
                      style={{
                        color: isActive ? "#F5F5F3" : "#5f5f5f",
                        fontWeight: 500,
                        letterSpacing: "0.02em",
                      }}
                    >
                      {s.short}
                    </span>
                  </div>

                  <div
                    className="absolute left-0 right-0 bottom-0"
                    style={{ height: "1px", background: "#171717" }}
                  />
                  {isActive && (
                    <div
                      key={`prog-${active}-${paused ? "p" : "r"}`}
                      className="absolute left-0 bottom-0 lr-step-progress"
                      data-paused={paused ? "true" : "false"}
                      style={{
                        height: "1px",
                        background: "#FF6A3D",
                        right: 0,
                      }}
                    />
                  )}
                </button>
                {i < SLIDES.length - 1 && (
                  <span
                    aria-hidden
                    className="text-xs md:text-sm shrink-0"
                    style={{ color: "#333", fontWeight: 300, opacity: 0.9 }}
                  >
                    →
                  </span>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* SLIDESHOW — coverflow with peek + glass frame. */}
      <div
        className="relative w-full mx-auto"
        style={{
          maxWidth: "min(100%, 1120px)",
          height: "clamp(240px, 42vw, 620px)",
        }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        data-testid="ai-pipeline-viewport"
      >
        {SLIDES.map((s, i) => {
          const offset = i - active;
          const isActive = offset === 0;
          const isNear = Math.abs(offset) === 1;
          const translate = offset * 62;
          const scale = isActive ? 1 : 0.82;
          const opacity = isActive ? 1 : isNear ? 0.28 : 0;
          return (
            <div
              key={s.id}
              data-testid={`ai-slide-${s.id}`}
              data-active={isActive ? "true" : "false"}
              className="absolute inset-0 flex items-center justify-center"
              style={{
                transform: `translateX(${translate}%) scale(${scale})`,
                opacity,
                filter: isActive ? "none" : "blur(6px) saturate(0.85)",
                zIndex: isActive ? 10 : isNear ? 3 : 1,
                pointerEvents: isActive ? "auto" : "none",
                transition:
                  "transform 800ms cubic-bezier(0.22, 0.61, 0.36, 1), opacity 700ms ease, filter 700ms ease",
                willChange: "transform, opacity",
              }}
            >
              <div
                className="relative w-full h-full overflow-hidden"
                style={{
                  borderRadius: "18px",
                  background:
                    "linear-gradient(180deg, rgba(20,20,20,0.85) 0%, rgba(11,11,11,0.9) 100%)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  boxShadow: isActive
                    ? "0 40px 120px -30px rgba(0,0,0,0.9), 0 0 0 1px rgba(255,255,255,0.03) inset, 0 0 60px -20px rgba(255,106,61,0.08)"
                    : "0 20px 60px -30px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.03) inset",
                  backdropFilter: "blur(20px)",
                  WebkitBackdropFilter: "blur(20px)",
                }}
              >
                {/* Fine top rail — internal-build chrome, no OS buttons. */}
                <div
                  aria-hidden
                  className="absolute top-0 left-0 right-0 flex items-center justify-between px-5 py-3 pointer-events-none"
                  style={{
                    borderBottom: "1px solid rgba(255,255,255,0.03)",
                    background:
                      "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0))",
                    zIndex: 2,
                  }}
                >
                  <span
                    className="text-[9px] tracking-[0.32em] uppercase"
                    style={{ color: "rgba(245,245,243,0.35)", fontWeight: 500 }}
                  >
                    Room
                  </span>
                  <span
                    className="text-[9px] tracking-[0.32em] uppercase"
                    style={{ color: "rgba(245,245,243,0.25)", fontWeight: 500 }}
                  >
                    Internal build
                  </span>
                </div>

                <img
                  src={s.src}
                  alt={s.label}
                  draggable={false}
                  className="absolute inset-0 w-full h-full object-contain"
                  style={{
                    padding:
                      "38px clamp(10px, 1.6vw, 26px) clamp(96px, 12vw, 150px)",
                  }}
                />

                {/* CAPTION OVERLAY — sits inside the active slide, elegant
                    bottom-left block over a soft gradient so the viewer sees
                    the stage immediately. */}
                <div
                  aria-hidden
                  className="absolute bottom-0 left-0 right-0 pointer-events-none"
                  style={{
                    height: "48%",
                    background:
                      "linear-gradient(180deg, rgba(8,8,8,0) 0%, rgba(8,8,8,0.55) 55%, rgba(8,8,8,0.9) 100%)",
                    zIndex: 3,
                  }}
                />
                <div
                  className="absolute left-0 right-0 bottom-0 pointer-events-none"
                  style={{ zIndex: 4 }}
                  data-testid={`ai-slide-overlay-${s.id}`}
                >
                  <div
                    className="px-6 md:px-8 pb-5 md:pb-7"
                    style={{ maxWidth: "min(100%, 720px)" }}
                  >
                    <div
                      className="text-[10px] md:text-[11px] tracking-[0.34em] uppercase"
                      style={{ color: "rgba(245,245,243,0.75)", fontWeight: 500 }}
                    >
                      <span style={{ color: "#FF6A3D" }}>
                        {String(i + 1).padStart(2, "0")}&nbsp;/&nbsp;{String(SLIDES.length).padStart(2, "0")}
                      </span>
                      &nbsp;&nbsp;—&nbsp;&nbsp;{s.label}
                    </div>
                    <div
                      className="mt-2 md:mt-3"
                      style={{
                        color: "#F5F5F3",
                        fontSize: "clamp(1.1rem, 1.8vw, 1.65rem)",
                        letterSpacing: "-0.02em",
                        fontWeight: 500,
                        lineHeight: 1.1,
                      }}
                    >
                      {s.label}
                    </div>
                    <div
                      className="mt-1.5 md:mt-2"
                      style={{
                        color: "rgba(245,245,243,0.72)",
                        fontSize: "clamp(0.85rem, 1.05vw, 1rem)",
                        fontWeight: 400,
                        lineHeight: 1.5,
                        letterSpacing: "-0.005em",
                        maxWidth: "560px",
                      }}
                    >
                      {s.caption}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const AISection = () => {
  return (
    <section
      id="ai"
      data-testid="ai-section"
      className="relative w-full pt-16 md:pt-24 pb-16 md:pb-24"
      style={{ background: "rgba(8, 8, 8, 0.9)" }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div
          className="lr-ai-intro-grid"
          data-testid="ai-intro-grid"
        >
          <div className="lr-ai-copy">
            <h2
              className="leading-[0.95] max-w-4xl"
              style={{
                fontSize: "clamp(2.5rem, 6.5vw, 5.5rem)",
                letterSpacing: "-0.04em",
                fontWeight: 500,
                color: "#F5F5F3",
              }}
              data-testid="ai-headline"
            >
              AI Production Lab.
            </h2>

            <div className="mt-8 md:mt-10 max-w-3xl">
              <p
                className="leading-[1.05]"
                style={{
                  fontSize: "clamp(1.5rem, 2.6vw, 2.4rem)",
                  letterSpacing: "-0.025em",
                  color: "#F5F5F3",
                  fontWeight: 500,
                }}
                data-testid="ai-tagline"
              >
                From live-action experience<br className="hidden md:block" /> to AI-native series.
              </p>
            </div>

            <div className="mt-12 md:mt-16 max-w-2xl">
              <p
                className="leading-[1.55]"
                style={{
                  fontSize: "clamp(1.1rem, 1.5vw, 1.4rem)",
                  letterSpacing: "-0.005em",
                  color: "#F5F5F3",
                  opacity: 0.9,
                  fontWeight: 400,
                }}
                data-testid="ai-paragraph-1"
              >
                Our work in vertical drama taught us what matters: the hook,
                the face, the wardrobe, the location, the frame, the emotional
                turn and the cliffhanger.
              </p>
              <p
                className="mt-6 leading-[1.55]"
                style={{
                  fontSize: "clamp(1.1rem, 1.5vw, 1.4rem)",
                  letterSpacing: "-0.005em",
                  color: "#F5F5F3",
                  opacity: 0.9,
                  fontWeight: 400,
                }}
                data-testid="ai-paragraph-2"
              >
                We built our AI Production Lab around those proven production
                best practices &mdash; turning real on-set knowledge into a
                controlled pipeline for AI-native vertical series.
              </p>
            </div>
          </div>

          <div className="lr-ai-video">
            <InternalBuildVideo />
          </div>
        </div>

        {/* One controlled visual module — a live view into the pipeline. */}
        <div className="mt-16 md:mt-24">
          <PipelineSlider />
        </div>

        {/* Closing line — reinforces the same idea in one calm sentence. */}
        <div className="mt-20 md:mt-28 max-w-3xl">
          <p
            className="leading-[1.4]"
            style={{
              fontSize: "clamp(1.15rem, 1.6vw, 1.5rem)",
              letterSpacing: "-0.01em",
              color: "#F5F5F3",
              opacity: 0.9,
              fontWeight: 500,
            }}
            data-testid="ai-paragraph-close"
          >
            A full workflow designed for consistency, speed and cinematic
            control.
          </p>
        </div>
      </div>
    </section>
  );
};

export default AISection;
